import React, { Suspense } from "react";
import MiniDrawer from "./Drawer/Menu/Menu";
import AppRoutes from "./Drawer/Routes";
import { Box } from "@mui/material";
import MainLayout from "./Pages/Main";
import { BASENAME } from "./config";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import LoginProvider from "./Pages/Login/LoginProvider";
import withAppProviders from "./withAppProviders";
import ChatWidget from "./Components/ChatWidget";
import { Loader } from "./Components/Loader";
import GlobalLoader from "./Components/Loader/GlobalLoader";
import useCompanyBoot from "./Core/hook/useCompanyBoot";

const ResetPasswordPage = React.lazy(() => import("./Pages/ResetPassword"));
const ForgotPasswordPage = React.lazy(() => import("./Pages/ForgotPassword"));
const AuthPage = React.lazy(() => import("./Pages/Login"));

/**
 * A path is public (never wrapped in the authenticated app layout)
 * when it matches the login / password-reset routes. Historically
 * `/login` was NOT listed here — so a stale admin session that
 * navigated to `/login` after a Staff reset landed inside the
 * authenticated shell with no matching child route → 404 wrapped
 * in the admin sidebar. Adding `/login` here makes the public login
 * page render regardless of the persisted auth state.
 */
const PUBLIC_PATH_PREFIXES = ["/login", "/reset-password", "/forgot-password"];
const isPublicPath = (pathname: string): boolean =>
  PUBLIC_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

const AppContent: React.FC = () => {
  const location = useLocation();
  // White-label boot: hydrate company branding, set document.title, swap
  // favicon, and publish brand color CSS variables. Runs before login so
  // the login screen shows the tenant's name/logo/background.
  useCompanyBoot();

  if (isPublicPath(location.pathname)) {
    return (
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route path="/login" element={<AuthPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        </Routes>
      </Suspense>
    );
  }

  // Authenticated app
  return (
    <LoginProvider>
      <Box>
        {/* Batch 45 — removed NotificationSafetyBanner. The yellow filled
           Alert sat in flow above the fixed sidebar+AppBar and its top
           edge poked through as a small yellow arc at the sidebar/header
           junction on every route. The dev-safety guarantee still stands
           at the backend (NotificationSafety middleware); the UI badge is
           no longer surfaced. If a compact developer-only indicator is
           wanted later, add it as a small chip inside the AppBar rather
           than a full-width bar. */}
        <GlobalLoader />
        <MiniDrawer>
          <AppRoutes />
        </MiniDrawer>
        <ChatWidget />
      </Box>
    </LoginProvider>
  );
};

const App: React.FC = () => (
  <Box data-testid="pos-ui-app">
    <MainLayout>
      <BrowserRouter basename={BASENAME}>
        <AppContent />
      </BrowserRouter>
    </MainLayout>
  </Box>
);

export default withAppProviders(App);
